import React, { useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as uiSlice from '../../../data/ui.slice';
import * as passengerSlice from '../../../data/passenger.slice';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import passengerUtils from '../../../services/passengerUtils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { Passenger } from '../../../data/models/passenger.model';
import ProfileForm from './ProfileForm';
import { Client } from '../../../data/models/client.model';
import { Gender } from '../../../data/models/gender.model';
import { PassengerAuthorizationReason } from '../../../data/models/passenger-authorization-reason.model';
import ConfirmationDialog from '../../ConfirmationDialog';

const PREFIX = 'PassengerProfileDialog';

const classes = {
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: 460,
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    marginTop: theme.spacing(0),
  },
}));

interface PassengerProfileDialogProps {
  passenger?: Passenger;
  title: string;
  portalClientBC?: string;
  clients: Client[];
  genders: Gender[];
  passengerAuthorizationReasons: PassengerAuthorizationReason[];
  open: boolean;
  close: () => void;
}

export default function PassengerProfileDialog({
  passenger,
  title,
  portalClientBC,
  clients,
  genders,
  passengerAuthorizationReasons,
  open,
  close,
}: PassengerProfileDialogProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const savedPassenger = useAppSelector(passengerSlice.selectCurrentPassenger);
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const savingPassenger = useAppSelector(passengerSlice.selectSavingPassenger);
  const [confirmDialogTitle, setConfirmDialogTitle] = React.useState('');
  const [confirmDialogMessage, setConfirmDialogMessage] = React.useState('');
  const [confirmDialogPostiveFn, setConfirmDialogPostiveFn] = React.useState<
    () => void
  >(() => null);
  const [confirmDialogNegativeFn, setConfirmDialogNegativeFn] = React.useState<
    () => void
  >(() => null);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

  const defaultValues = useMemo(
    () => passengerUtils.getProfileDefaults(passenger),
    [passenger],
  );
  const formHook = useForm({
    mode: 'onTouched',
    defaultValues,
  });

  const {
    formState: { isDirty },
  } = formHook;

  React.useEffect(() => {
    const defaults = passengerUtils.getProfileDefaults(passenger);
    formHook.reset(defaults);
  }, [passenger]);

  useEffect(() => {
    formHook.reset(defaultValues);
  }, [defaultValues]);

  useEffect(() => {
    if (savedPassenger && saveSuccessMessage) {
      handleFormclose();
      navigate(`/passengers/${savedPassenger?.passengerUUID}`);
    }
  }, [savedPassenger, saveSuccessMessage]);

  const cancel = () => {
    setConfirmDialogOpen(false);
  };

  const confirm = () => {
    formHook.reset();
    close();
    setConfirmDialogOpen(false);
  };

  const handleFormclose = () => {
    close();
    formHook.reset();
  };

  const closeModal = () => {
    setConfirmDialogOpen(true);
    setConfirmDialogTitle(t('passenger.confirmCancelCreatePassengerTitle'));
    setConfirmDialogMessage(t('passenger.confirmCancelCreatePassengerMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
  };

  const isFormDirty = () => {
    if (isDirty) {
      closeModal();
    } else {
      confirm();
    }
  };

  const handleSave = (data: any) => {
    const passengerData = passengerUtils.getPassengerFromFormData(
      data,
      passenger,
    );
    if (passengerData.passengerUUID) {
      dispatch(passengerSlice.updatePassengerAsync(passengerData));
    } else {
      dispatch(passengerSlice.createPassengerAsync(passengerData));
    }
  };

  return (
    <RootDialog open={open} onClose={isFormDirty}>
      <DialogTitle>
        {title}
        {savingPassenger && (
          <LinearProgress style={{ width: '100%' }} color="primary" />
        )}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <ProfileForm
            currentClientBC={passenger?.clientBC}
            portalClientBC={portalClientBC}
            clients={clients}
            genders={genders}
            passengerAuthorizationReasons={passengerAuthorizationReasons}
            formHook={formHook}
          />
        </LocalizationProvider>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={isFormDirty}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            !formHook.formState.isValid ||
            !formHook.formState.isDirty ||
            savingPassenger
          }
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.create')}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        title={confirmDialogTitle}
        message={confirmDialogMessage}
        open={confirmDialogOpen}
        positiveAction={confirmDialogPostiveFn}
        negativeAction={confirmDialogNegativeFn}
      />
    </RootDialog>
  );
}
